// src/services/AdminServices.js
import axios from "axios";
import { sendEmail } from "./EmailSender";
import UtilMethods from "../util/UtilMethods";
import { jwtDecode } from "jwt-decode";

const API_URL = process.env.REACT_APP_API_URL;
// Create an axios instance
export const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});
let isRefreshingGuestToken = false; // Flag to prevent multiple refreshes

const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const { exp } = jwtDecode(token);
    return Date.now() >= exp * 1000; // Convert expiration to milliseconds
  } catch (e) {
    console.error("Failed to decode token:", e);
    return true; // Treat decoding failure as expired
  }
};

axiosInstance.interceptors.request.use(
  async (config) => {
    let token = localStorage.getItem("token");

    // If a valid user token exists, use it
    if (token && !isTokenExpired(token)) {
      config.headers["Authorization"] = `Bearer ${token}`;
      return config;
    }

    // If no user token, check guest token
    let guestToken = localStorage.getItem("guestToken");

    if (!guestToken || isTokenExpired(guestToken)) {
      if (!isRefreshingGuestToken) {
        isRefreshingGuestToken = true;
        console.log("Guest token expired. Fetching a new one...");

        try {
          const response = await axios.post(`${API_URL}/getToken`);
          guestToken = response.data.token;
          localStorage.setItem("guestToken", guestToken);
        } catch (error) {
          console.error("Failed to fetch guest token:", error);
          return Promise.reject(error);
        } finally {
          isRefreshingGuestToken = false;
        }
      } else {
        // Wait until another request finishes fetching the guest token
        while (isRefreshingGuestToken) {
          await new Promise((resolve) => setTimeout(resolve, 100)); // Poll every 100ms
        }
        guestToken = localStorage.getItem("guestToken"); // Get updated token
      }
    }

    if (guestToken) {
      config.headers["Authorization"] = `Bearer ${guestToken}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

const registerUser = async (userData) => {
  try {
    //console.log('registerUser',userData);
    // const response = await axios.post(`${API_URL}/register`, userData);
    const response = await axiosInstance.post(`/register`, userData);
    sendEmail(userData, "welcome"); // Send registration email
    return response.data; // Return the response data
  } catch (error) {
    throw new Error(error.response?.data.message || "Error registering user");
  }
};

const updateUser = async (userData) => {
  try {
    //console.log(" updating user",userData)
    // const response = await axios.put(`${API_URL}/updateUser`, userData);
    const response = await axiosInstance.put(`/updateUser`, userData);
    return response.data; // Return the response data
  } catch (error) {
    console.error("Error updating user", error);
    throw new Error(error.response?.data.message || "Error updating user");
  }
};
const changePassword = async (phone, newPassword) => {
  try {
    console.log(" changing password", phone);

    //const response = await axios.post(`${API_URL}/changePassword`, {
    const response = await axiosInstance.post(`/changePassword`, {
      phone: phone,
      password: newPassword,
    });
    return response.data; // Return the response data
  } catch (error) {
    console.error("Error changing password", error);
    throw new Error(error.response?.data.message || "Error changing password");
  }
};
// Add login function
const loginUser = async (email, password) => {
  console.log(" loginUser email", email);
  try {
    const response = await axios.post(`${API_URL}/login`, {
      email,
      password,
    });
    //console.log(" loginUser response.data",response.data)
    return response.data; // Return the response data
  } catch (error) {
    console.error("Error loginUser user :", error);
    throw error;
  }
};

const fetchUsers = async () => {
  try {
    // const response = await axios.get(`${API_URL}/users`);
    //console.log("fetchUsers token:",localStorage.getItem('token'));
    const response = await axiosInstance.get(`/users`);
    return response.data; // Return the response data
  } catch (error) {
    console.error("Error fetching users", error);
    throw new Error("Error fetching users.");
  }
};

const deleteUser = async (userId) => {
  try {
    await axiosInstance.delete(`/users/${userId}`);
    // await axios.delete(`${API_URL}/users/users/${userId}`);
  } catch (error) {
    throw new Error("Error deleting user.");
  }
};

const sendOTP = async (email, phone, retreatID, mode) => {
  console.log(
    "sendOTP request initiated for email:",
    email,
    "phone:",
    phone,
    "mode : ",
    mode,
    "retreatID : ",
    retreatID
  );
  let response;
  try {
    if (mode === "RETREAT") {
      response = await axios.post(`${API_URL}/send-verification-retreat`, {
        email,
        phone: phone,
        retreatID: retreatID,
        mode: mode,
      });
    } else {
      response = await axios.post(`${API_URL}/send-verification`, {
        email,
        phone: phone,
        mode: mode,
      });
    }

    console.log("sendOTP response:", response.data);
    return response.data; // Return the response data on success
  } catch (error) {
    console.error(
      `Error in send-verification for email: ${email}, phone: ${phone}`,
      error
    );

    if (error.response) {
      // Extract server error message
      const serverErrorMessage =
        error.response.data?.error ||
        "Failed to send OTP. Please try again later.";

      if (error.response.status === 401) {
        throw new Error(
          "OTP Unauthorized access. Please contact info@graceanointing.org"
        );
      } else if (error.response.status === 409) {
        // this error will not come for retreat
        console.log(
          "send-verification error response:",
          error.response.data.data
        );

        // Create a custom error object with additional data
        const customError = new Error(
          `${error.response.data.message}, Info: ${JSON.stringify(
            error.response.data.data.email || error.response.data.data.phone
          )}`
        );

        // Add custom data to the error object
        customError.responseData = error.response.data.data.groupData;
        customError.statusCode = error.response.status;

        throw customError;
      }

      // Other server-side errors
      throw new Error(serverErrorMessage);
    } else if (error.request) {
      // No response received
      console.error("send-verification error request:", error.request);
      throw new Error(
        "No response received from the server. Please check your network."
      );
    } else {
      // Unexpected errors
      console.error("send-verification error message:", error.message);
      throw new Error("An unexpected error occurred while sending OTP.");
    }
  }
};

// Add verifyOTP function
const verifyOTP = async (otp, email, phone, mode, retreatID) => {
  console.log("verifyOTP ", otp);
  console.log("verifyOTP phone", phone);
  try {
    const response = await axios.post(`${API_URL}/check-verification`, {
      otp,
      email,
      phone,
      mode,
      retreatID,
    });
    console.log("verifyOTP response.data", response.data);
    console.log("verifyOTP response.status", response.status);
    return response;
  } catch (error) {
    console.error("Error", error);
    throw new Error(error.response?.data?.message || "Error verifying OTP");
  }
};

const fetchSabbathDetails = async (params) => {
  console.log("fetchSabbathDetails params", params);
  try {
    UtilMethods.log("info", "fetchSabbathDetails params", params);
    const response = await axiosInstance.get(`/sabbathList`, { params });
    console.log("fetchSabbathDetails response", response);
    return response.data;
  } catch (error) {
    console.error("Error fetching sabbath details", error);
    throw new Error("Error fetching sabbath details.");
  }
};

const updateSabbathDetails = async (sabbathData) => {
  try {
    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/sabbath/${sabbathData.id}`,
      sabbathData
    );
    return response;
  } catch (error) {
    console.error("Error updating sabbath details", error);
    throw new Error("Error updating sabbath details.");
  }
};
const registerSabbath = async (formData) => {
  try {
    const response = await axios.post(`${API_URL}/sabbath/register`, formData);
    return response;
  } catch (error) {
    console.error("Error registering sabbath", error);
    throw {
      code: error.response?.status || 500,
      details: error.response?.data?.details || "Error registering sabbath."
    };
  }
};
const updateApprovalDetails = async (
  approvedBy,
  accessToken,
  meetingId,
  record
) => {
  try {
    console.log("updateApprovalDetails", record);
    const id = record?.id;
    const type = record?.type;

    console.log(
      "updateApprovalDetails",
      id,
      approvedBy,
      accessToken,
      meetingId,
      type
    );
    const response = await axiosInstance.put(`/sabbath/${id}/status`, {
      status: "Approved",
      approvedBy: approvedBy,
      accessToken: accessToken,
      meetingId: meetingId,
      type: type,
    });
    UtilMethods.log(
      "info",
      "Sabbath status updated successfully",
      response.data
    );
    return response.data;
  } catch (error) {
    console.error("Error approving sabbath status", error);
    throw new Error("Error approving sabbath status : " + error?.response?.data?.error);
  }
};
const deleteSabbathDetails = async (
  approvedBy,
  accessToken,
  meetingId,
  record
) => {
  try {
    console.log("deleteSabbathDetails", record);
    const id = record?.id;
    const type = record?.type;
    console.log("deleteSabbathDetails meetingId", meetingId);
    const response = await axiosInstance.put(`/sabbath/${id}/status`, {
      status: "Deleted",
      approvedBy: approvedBy,
      accessToken: accessToken,
      meetingId: meetingId,
      type: type,
    });
    console.log("deleteSabbathDetails response", response.data);
    return response.data;
  } catch (error) {
    console.error("Error deleting sabbath", error);
    throw new Error("Error deleting sabbath : " + error?.response?.data?.error);
  }
};
const uploadMedia = async (mediaData) => {
  try {
    const response = await axiosInstance.post(`/gallery`, mediaData);
    console.log("uploadMedia response", response);
    return response.data;
  } catch (error) {
    console.error("Error uploading media", error);
    const serverMessage =
      error.response?.data?.message ||
      error.response?.data?.details ||
      "Error uploading media";
    throw new Error(serverMessage);
  }
};
const fetchMedia = async () => {
  try {
    const response = await axiosInstance.get(`/gallery`);
    console.log("fetchMedia response", response);
    return response.data;
  } catch (error) {
    console.error("Error fetching media", error);
    throw new Error("Error fetching media.");
  }
};

const unsubscribe = async (email) => {
  try {
    console.log("Unsubscribing email:", email);
    const response = await axiosInstance.post(`/unsubscribe`, { email });
    return response.data;
  } catch (error) {
    console.error("Error unsubscribing", error);
    throw new Error("Error unsubscribing.");
  }
};
const updateSubscriptions = async (formData) => {
  try {
    console.log("updateSubscriptions formData", formData);
    // Use axiosInstance so guest/user tokens are applied consistently
    let response;
    try {
      // Try POST (create) first
      response = await axiosInstance.post(`/subscription`, formData);
    } catch (err) {
      // If route not found, try PUT as some backends expect PUT
      if (err.response && err.response.status === 404) {
        console.warn('/subscription POST returned 404, trying PUT as fallback');
        response = await axiosInstance.put(`/subscription`, formData);
      } else {
        throw err;
      }
    }

    console.log("updateSubscriptions response", response);
    return response;
  } catch (error) {
    console.error("Error updating subscription form", error);
    if (error.response && error.response.status === 404) {
      throw new Error(`Subscription endpoint not found (404). Check backend at ${API_URL}/subscription`);
    }
    const serverMessage = error.response?.data?.message || error.message || "Error updating subscription form.";
    throw new Error(serverMessage);
  }
};

export {
  uploadMedia,
  fetchMedia,
  fetchUsers,
  updateUser,
  deleteUser,
  registerUser,
  loginUser,
  sendOTP,
  verifyOTP,
  changePassword,
  fetchSabbathDetails,
  updateSabbathDetails,
  updateApprovalDetails,
  registerSabbath,
  deleteSabbathDetails,
  updateSubscriptions,
  unsubscribe,
};
